/* ============================================================================
   orientation.js — quaternion-based device-orientation module ("Look").

   • Builds a unit quaternion from the W3C DeviceOrientationEvent (ZXY intrinsic)
     and composes it with the screen-orientation correction.  Cookies are stored
     as world-frame unit vectors and projected through the live quaternion every
     frame, so they stay pinned to the room under pan / tilt / roll.
   • Consumes a SINGLE orientation source.  Android fires both `deviceorientation`
     (relative, gyro-integrated — drifts) and `deviceorientationabsolute`
     (magnetometer-referenced — no drift).  Feeding both into one target makes the
     view slide between two references each frame, which reads as cookies drifting
     across the room.  We prefer the absolute stream and ignore the relative one
     as soon as it appears.
   • Exponential low-pass (nlerp) on the quaternion keeps latency low.

   Mobile only — there is no touch/mouse fallback; the game requires real sensors.
   ========================================================================= */
(function(){
'use strict';
const Look = window.Look = {};
const D2R = Math.PI/180;
const clamp = (v,a,b)=>v<a?a:v>b?b:v;

/* ---- quaternion utils -------------------------------------------------- */
function qmul(a,b){
  const aw=a[0],ax=a[1],ay=a[2],az=a[3], bw=b[0],bx=b[1],by=b[2],bz=b[3];
  return [aw*bw-ax*bx-ay*by-az*bz,
          aw*bx+ax*bw+ay*bz-az*by,
          aw*by-ax*bz+ay*bw+az*bx,
          aw*bz+ax*by-ay*bx+az*bw];
}
function qconj(q){ return [q[0],-q[1],-q[2],-q[3]]; }
function vrot(q,v){
  const w=q[0],x=q[1],y=q[2],z=q[3], vx=v[0],vy=v[1],vz=v[2];
  const tx=2*(y*vz - z*vy);
  const ty=2*(z*vx - x*vz);
  const tz=2*(x*vy - y*vx);
  return [vx + w*tx + (y*tz - z*ty),
          vy + w*ty + (z*tx - x*tz),
          vz + w*tz + (x*ty - y*tx)];
}
function qaxis(ax,ay,az,theta){ const s=Math.sin(theta/2); return [Math.cos(theta/2), ax*s, ay*s, az*s]; }
function qnorm(q){ const n=Math.hypot(q[0],q[1],q[2],q[3])||1; return [q[0]/n,q[1]/n,q[2]/n,q[3]/n]; }
function nlerp(a,b,t){
  let d=a[0]*b[0]+a[1]*b[1]+a[2]*b[2]+a[3]*b[3];
  if(d<0) b=[-b[0],-b[1],-b[2],-b[3]];
  return qnorm([a[0]+(b[0]-a[0])*t, a[1]+(b[1]-a[1])*t,
                a[2]+(b[2]-a[2])*t, a[3]+(b[3]-a[3])*t]);
}
// W3C ZXY intrinsic: q = qZ(alpha) * qX(beta) * qY(gamma).  Returns DEVICE-TO-WORLD.
function qFromDevOrient(alphaDeg, betaDeg, gammaDeg){
  const a=alphaDeg*D2R, b=betaDeg*D2R, g=gammaDeg*D2R;
  const cZ=Math.cos(a/2), sZ=Math.sin(a/2);
  const cX=Math.cos(b/2), sX=Math.sin(b/2);
  const cY=Math.cos(g/2), sY=Math.sin(g/2);
  return [cZ*cX*cY - sZ*sX*sY,
          cZ*sX*cY - sZ*cX*sY,
          cZ*cX*sY + sZ*sX*cY,
          sZ*cX*cY + cZ*sX*sY];
}

/* ---- state ------------------------------------------------------------- */
let qTarget=[1,0,0,0];
let qView  =[1,0,0,0];
let qScreen=[1,0,0,0];
let calibrated=false;
let useGyro=false;
let inited=false;
let absoluteActive=false;

Look.TAU = 0.06;

/* ---- screen orientation ------------------------------------------------ */
function updateScreenCorr(){
  let ang = 0;
  if(screen && screen.orientation && screen.orientation.angle != null) ang = screen.orientation.angle;
  else if(typeof window.orientation === 'number') ang = window.orientation;
  qScreen = qaxis(0,0,1, -ang*D2R);
}

/* ---- sensor handlers (single source) ----------------------------------- */
function ingest(e){
  if(e.alpha==null && e.beta==null && e.gamma==null) return;
  const qSens = qFromDevOrient(e.alpha||0, e.beta||0, e.gamma||0);
  qTarget = qmul(qScreen, qSens);
  if(!calibrated){ qView = qTarget; calibrated = true; }
  useGyro = true;
}
function onAbsolute(e){
  if(e.alpha==null && e.beta==null && e.gamma==null) return;
  absoluteActive = true;
  window.removeEventListener('deviceorientation', onRelative, true);
  ingest(e);
}
function onRelative(e){
  if(absoluteActive) return;
  ingest(e);
}

Look.init = function(){
  if(inited) return; inited = true;
  updateScreenCorr();
  window.addEventListener('orientationchange', updateScreenCorr);
  if(screen.orientation && screen.orientation.addEventListener)
    screen.orientation.addEventListener('change', updateScreenCorr);
  window.addEventListener('deviceorientationabsolute', onAbsolute, true);
  window.addEventListener('deviceorientation', onRelative, true);
};

Look.requestPermission = async function(){
  try{
    if(typeof DeviceOrientationEvent!=='undefined' &&
       typeof DeviceOrientationEvent.requestPermission==='function'){
      const r = await DeviceOrientationEvent.requestPermission();
      return r==='granted';
    }
    return true;
  }catch(_){ return false; }
};

Look.calibrated = ()=>calibrated;
Look.usingGyro  = ()=>useGyro;

/* ---- per-frame smoothing ------------------------------------------------ */
Look.update = function(dt){
  const a = 1 - Math.exp(-dt/Look.TAU);
  qView = nlerp(qView, qTarget, a);
};

/* ---- projection / spawn ------------------------------------------------ */
Look.project = function(vWorld, focal, W, H){
  const v = vrot(qconj(qView), vWorld);
  const depth = -v[2];
  if(depth <= 0.05) return {x:0,y:0,depth:depth,on:false};
  const cx=W/2, cy=H/2;
  const x = cx + (v[0]/depth)*focal;
  const y = cy - (v[1]/depth)*focal;
  return {x, y, depth, on: Math.abs(x-cx)<W*0.5+140 && Math.abs(y-cy)<H*0.5+140};
};

Look.spawnVector = function(dYawDeg, dPitchDeg){
  const dy = dYawDeg*D2R, dp = dPitchDeg*D2R;
  const cy=Math.cos(dy), sy=Math.sin(dy), cp=Math.cos(dp), sp=Math.sin(dp);
  const vScreen = [sy*cp, sp, -cy*cp];
  return vrot(qView, vScreen);
};

Look.angBetween = function(a,b){
  return Math.acos(clamp(a[0]*b[0]+a[1]*b[1]+a[2]*b[2], -1, 1));
};

})();
